import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '@/hooks/usePortalAuth';
import { useInspectorProjects } from '@/hooks/useInspectorProjects';
import { useInspections } from '@/hooks/useInspections';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Upload, CalendarIcon, X, Image as ImageIcon, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const INSPECTION_TYPES = [
  'Veiligheid',
  'Onderhoud',
  'Installatie',
  'Keuring',
  'Controle',
  'Anders'
];

export default function InspectorUpload() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { portalUser } = usePortalAuth();
  const { data: projects, isLoading: projectsLoading } = useInspectorProjects(portalUser?.id);
  const { createInspection } = useInspections();

  const [step, setStep] = useState<'info' | 'photos' | 'review' | 'success'>('info');
  const [projectId, setProjectId] = useState('');
  const [type, setType] = useState('');
  const [customType, setCustomType] = useState('');
  const [date, setDate] = useState<Date>(new Date());
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      
      // Validate file types and sizes
      const validFiles = newFiles.filter(file => {
        const isImage = file.type.startsWith('image/');
        const isUnder25MB = file.size <= 25 * 1024 * 1024;
        
        if (!isImage) {
          toast({
            title: 'Ongeldig bestand',
            description: `${file.name} is geen afbeelding`,
            variant: 'destructive',
          });
          return false;
        }
        
        if (!isUnder25MB) {
          toast({
            title: 'Bestand te groot',
            description: `${file.name} is groter dan 25MB`,
            variant: 'destructive',
          });
          return false;
        }
        
        return true;
      });
      
      setPhotos(prev => [...prev, ...validFiles]);
    }
  };

  const removePhoto = (index: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!projectId || !type || photos.length === 0) {
      toast({
        title: 'Ontbrekende velden',
        description: 'Vul alle verplichte velden in en upload minimaal 1 foto',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);

    try {
      // Upload photos to storage
      const uploadedUrls: string[] = [];
      
      for (const photo of photos) {
        const fileExt = photo.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${portalUser?.id}/${projectId}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('portal-uploads')
          .upload(filePath, photo);

        if (uploadError) throw uploadError;

        // Store the file path instead of public URL for private bucket
        uploadedUrls.push(filePath);
      }

      // Create inspection record
      const selectedProject = projects?.find(p => p.id === projectId);
      
      await createInspection({
        tenant_id: selectedProject?.org_id || null,
        project_id: projectId,
        inspector_id: portalUser?.id || '',
        date: format(date, 'yyyy-MM-dd'),
        type: type === 'Anders' ? customType : type,
        notes: notes || null,
        photo_urls: uploadedUrls,
        status: 'ingediend',
      });

      setStep('success');
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: 'Fout bij uploaden',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <Card>
          <CardContent className="pt-12 pb-12 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-6">
              <CheckCircle2 className="h-10 w-10 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Inspectie Ingediend!</h2>
            <p className="text-muted-foreground mb-8">
              Je inspectie met {photos.length} foto's is succesvol ingediend en wordt nu verwerkt door het CRM-team.
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/inspector')}>
                Terug naar Dashboard
              </Button>
              <Button variant="outline" onClick={() => {
                setStep('info');
                setProjectId('');
                setType('');
                setCustomType('');
                setDate(new Date());
                setNotes('');
                setPhotos([]);
              }}>
                Nieuwe Inspectie
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle>Nieuwe Inspectie Uploaden</CardTitle>
          <CardDescription>
            Stap {step === 'info' ? '1' : step === 'photos' ? '2' : '3'} van 3: {
              step === 'info' ? 'Inspectie Details' :
              step === 'photos' ? 'Foto\'s Uploaden' :
              'Controleren & Verzenden'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step 1: Info */}
          {step === 'info' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="project">Project *</Label>
                {projectsLoading ? (
                  <div className="text-sm text-muted-foreground">Projecten laden...</div>
                ) : !projects || projects.length === 0 ? (
                  <div className="p-4 border rounded-md bg-muted">
                    <p className="text-sm text-muted-foreground">
                      Je hebt nog geen projecten toegewezen. Neem contact op met je beheerder.
                    </p>
                  </div>
                ) : (
                  <Select value={projectId} onValueChange={setProjectId}>
                    <SelectTrigger id="project" className="bg-background">
                      <SelectValue placeholder="Selecteer een project" />
                    </SelectTrigger>
                    <SelectContent className="bg-background z-50">
                      {projects.map(project => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name} {project.project_number ? `(#${project.project_number})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Type Inspectie *</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger id="type" className="bg-background">
                    <SelectValue placeholder="Selecteer type" />
                  </SelectTrigger>
                  <SelectContent className="bg-background z-50">
                    {INSPECTION_TYPES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {type === 'Anders' && (
                <div className="space-y-2">
                  <Label htmlFor="customType">Geef type aan *</Label>
                  <Input
                    id="customType"
                    value={customType}
                    onChange={(e) => setCustomType(e.target.value)}
                    placeholder="Bijv. Noodstroomtest"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Inspectiedatum *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal bg-background",
                        !date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, 'PPP', { locale: nl }) : 'Selecteer datum'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-background z-50" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => d && setDate(d)}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Opmerkingen</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Eventuele bijzonderheden of bevindingen..."
                  rows={4}
                />
              </div>

              <Button onClick={() => setStep('photos')} className="w-full" disabled={!projectId || !type || (type === 'Anders' && !customType)}>
                Volgende: Foto's Uploaden
              </Button>
            </>
          )}

          {/* Step 2: Photos */}
          {step === 'photos' && (
            <>
              <div className="space-y-4">
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="photo-upload"
                  />
                  <Label htmlFor="photo-upload" className="cursor-pointer">
                    <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-sm font-medium">Klik om foto's te uploaden</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      JPG, PNG tot 25MB per bestand
                    </p>
                  </Label>
                </div>

                {photos.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {photos.map((photo, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={URL.createObjectURL(photo)}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <Button
                          size="icon"
                          variant="destructive"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => removePhoto(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                          {(photo.size / 1024 / 1024).toFixed(1)} MB
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep('info')} className="flex-1">
                  Vorige
                </Button>
                <Button onClick={() => setStep('review')} className="flex-1" disabled={photos.length === 0}>
                  Volgende: Controleren
                </Button>
              </div>
            </>
          )}

          {/* Step 3: Review */}
          {step === 'review' && (
            <>
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-lg space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Project</p>
                    <p className="font-medium">{projects?.find(p => p.id === projectId)?.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Type</p>
                    <p className="font-medium">{type === 'Anders' ? customType : type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Datum</p>
                    <p className="font-medium">{format(date, 'PPP', { locale: nl })}</p>
                  </div>
                  {notes && (
                    <div>
                      <p className="text-sm text-muted-foreground">Opmerkingen</p>
                      <p className="font-medium">{notes}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Foto's</p>
                    <p className="font-medium">{photos.length} bestanden</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {photos.map((photo, index) => (
                    <img
                      key={index}
                      src={URL.createObjectURL(photo)}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-24 object-cover rounded"
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep('photos')} className="flex-1" disabled={uploading}>
                  Vorige
                </Button>
                <Button onClick={handleSubmit} className="flex-1" disabled={uploading}>
                  {uploading ? 'Uploaden...' : 'Verzenden'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
